/**
 * Zod schemas for the request/response shapes the api and the SPA both speak.
 *
 * NORMATIVE SOURCE: docs/05-api-spec.md. Field names come from
 * docs/02-domain-model.md via packages/shared/src/enums.ts and are never re-spelled
 * here. The api validates requests with these schemas; the SPA validates its forms
 * with the same objects, so a client cannot invent a value the server rejects.
 */
import { z } from 'zod';
import { AUDIT_ENTITY_TYPE, DATE_QUALIFIER, GENDER, NOTIFICATION_KIND, PROPOSAL_KIND, PROPOSAL_STATUS, VOTE_CHOICE, LIVING_STATUS, PARENT_BASIS, PARENT_ROLE, RELATION_KIND, RETIRE_REASON, SIBLING_BASIS, UNION_END_REASON, UNION_TYPE, USER_ROLE, USER_STATUS, DERIVED_KIND, } from './enums.js';
import { FUZZY_YEAR_MAX, FUZZY_YEAR_MIN, fuzzyDateNotAfter, isFuzzyDateEmpty, validateFuzzyDate, } from './fuzzy-date.js';
import { PAGE_LIMIT_DEFAULT, PAGE_LIMIT_MAX, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, VOTE_COMMENT_MAX_LENGTH, } from './constants.js';
/* ------------------------------------------------------------------ primitives */
export const uuidSchema = z.string().uuid();
export const emailSchema = z.string().trim().min(3).max(254).email();
export const displayNameSchema = z.string().trim().min(1).max(120);
/** docs/09 §2.1: ≥ 12 characters, no composition rules, ≤ 128 (hashing DoS bound). */
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
/**
 * FuzzyDate value object — docs/05 §1.3, ADR-0004.
 * `sort_key` is server-generated and rejected on input; the whole object is null
 * when every component is unknown. The rules themselves live in `fuzzy-date.ts`
 * so the form, the api and the database CHECKs cannot drift apart.
 */
export const fuzzyDateSchema = z
    .object({
    year: z.number().int().min(FUZZY_YEAR_MIN).max(FUZZY_YEAR_MAX).nullable().optional(),
    month: z.number().int().min(1).max(12).nullable().optional(),
    day: z.number().int().min(1).max(31).nullable().optional(),
    qualifier: z.enum(DATE_QUALIFIER).nullable().optional(),
    text: z.string().trim().max(200).nullable().optional(),
})
    .strict()
    .superRefine((date, ctx) => {
    for (const problem of validateFuzzyDate(date)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: problem,
            path: [FUZZY_PROBLEM_FIELD[problem]],
        });
    }
});
/** Which component a rule violation belongs to, for field-level form errors. */
const FUZZY_PROBLEM_FIELD = {
    month_requires_year: 'month',
    day_requires_month: 'day',
    qualifier_requires_year: 'qualifier',
    year_out_of_range: 'year',
    month_out_of_range: 'month',
    day_out_of_range: 'day',
    day_not_in_month: 'day',
    unknown_qualifier: 'qualifier',
};
/** Cursor pagination envelope — docs/05 §1.4. */
export const pageQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(PAGE_LIMIT_DEFAULT),
    cursor: z.string().max(512).optional(),
});
/* ----------------------------------------------------------------------- auth */
export const loginSchema = z
    .object({
    email: emailSchema,
    // Not `passwordSchema`: an existing password predating a policy change must
    // still be able to log in (and be rehashed), and length must never be an oracle.
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
})
    .strict();
export const acceptInviteSchema = z
    .object({
    token: z.string().min(8).max(512),
    password: passwordSchema,
    display_name: displayNameSchema.optional(),
})
    .strict();
export const changePasswordSchema = z
    .object({
    current_password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    new_password: passwordSchema,
})
    .strict();
export const passwordResetSchema = z
    .object({
    token: z.string().min(8).max(512),
    new_password: passwordSchema,
})
    .strict();
export const createInviteSchema = z
    .object({
    email: emailSchema,
    display_name: displayNameSchema,
    person_id: uuidSchema.optional(),
})
    .strict();
export const updateUserSchema = z
    .object({
    changes: z
        .object({
        display_name: displayNameSchema.optional(),
        status: z.enum(['active', 'suspended']).optional(),
        role: z.literal('admin').optional(),
        person_id: uuidSchema.nullable().optional(),
    })
        .strict()
        .refine((c) => Object.keys(c).length > 0, { message: 'no changes supplied' }),
})
    .strict();
export const listUsersQuerySchema = pageQuerySchema.extend({
    q: z.string().trim().min(1).max(120).optional(),
    status: z.union([z.enum(USER_STATUS), z.array(z.enum(USER_STATUS))]).optional(),
    role: z.union([z.enum(USER_ROLE), z.array(z.enum(USER_ROLE))]).optional(),
    has_person: z.enum(['true', 'false']).optional(),
    sort: z.enum(['display_name', 'created_at', '-created_at']).default('display_name'),
});
/* --------------------------------------------------------------------- persons */
/**
 * Free-text caps. The database CHECKs only that a *present* name is not blank
 * and `bio_note <= 10000` (docs/04 §5.2); the rest are app-level bounds that
 * keep a name field from becoming a paste buffer. Raising one is a schema change
 * here, never a silent truncation at the api.
 */
export const NAME_MAX_LENGTH = 200;
export const PLACE_MAX_LENGTH = 200;
export const BIO_NOTE_MAX_LENGTH = 10_000;
export const NOTE_MAX_LENGTH = 2_000;
export const SOURCE_NOTE_MAX_LENGTH = 500;
export const SUMMARY_MAX_LENGTH = 200;
/**
 * Every name field is optional, and none may be blank.
 *
 * A tree records people nobody alive can name — "my father's paternal
 * grandmother" is a position in the family with descendants and no name
 * attached. Forcing a placeholder there ("Unknown", "N.N.") makes nine such
 * people indistinguishable and puts a non-name into every search index. An
 * absent name is a fact; an empty string is an accident, so the trim-and-null
 * below turns one into the other rather than storing it (ARCHITECTURE.md §2.1
 * C23).
 */
const optionalName = z
    .string()
    .trim()
    .max(NAME_MAX_LENGTH)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
const optionalPlace = z.string().trim().max(PLACE_MAX_LENGTH).nullable().optional();
const sourceNoteSchema = z.string().trim().max(SOURCE_NOTE_MAX_LENGTH).nullable().optional();
const summarySchema = z.string().trim().min(1).max(SUMMARY_MAX_LENGTH);
/**
 * The writable person fields of docs/05 §5.4 — exactly these, no provenance and
 * no `portrait_photo_id` (photos own that, from M4).
 */
export const personWritableSchema = z
    .object({
    given_names: optionalName,
    family_name: optionalName,
    birth_family_name: optionalName,
    nickname: optionalName,
    gender: z.enum(GENDER),
    living_status: z.enum(LIVING_STATUS),
    birth: fuzzyDateSchema.nullable().optional(),
    death: fuzzyDateSchema.nullable().optional(),
    birth_place: optionalPlace,
    death_place: optionalPlace,
    bio_note: z.string().trim().max(BIO_NOTE_MAX_LENGTH).nullable().optional(),
})
    .strict();
export const personInputSchema = personWritableSchema
    .refine((p) => p.living_status === 'deceased' || isFuzzyDateEmpty(p.death), {
    message: 'death_requires_deceased',
    path: ['death'],
})
    .refine((p) => fuzzyDateNotAfter(p.birth, p.death), {
    message: 'death_before_birth',
    path: ['death'],
});
/** `null` endpoint = "the person being created" (docs/05 §5.4). */
export const relationWritableSchema = z
    .object({
    kind: z.enum(RELATION_KIND),
    person_a_id: uuidSchema.nullable(),
    person_b_id: uuidSchema.nullable(),
    parent_role: z.enum(PARENT_ROLE).nullable().optional(),
    parent_basis: z.enum(PARENT_BASIS).nullable().optional(),
    union_type: z.enum(UNION_TYPE).nullable().optional(),
    end_reason: z.enum(UNION_END_REASON).nullable().optional(),
    sibling_basis: z.enum(SIBLING_BASIS).nullable().optional(),
    started: fuzzyDateSchema.nullable().optional(),
    ended: fuzzyDateSchema.nullable().optional(),
    note: z.string().trim().max(NOTE_MAX_LENGTH).nullable().optional(),
    source_note: sourceNoteSchema,
})
    .strict();
/**
 * The kind-dependent column shape of docs/02 §2.2, which the `ra_kind_shape_check`
 * CHECK also enforces: each kind requires its own discriminators and forbids the
 * others', siblings carry no dates, and a union's end date needs an end reason.
 */
export const relationInputSchema = relationWritableSchema.superRefine((r, ctx) => {
    const require = (field, message) => {
        if (r[field] == null)
            ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [field] });
    };
    const forbid = (field, message) => {
        if (r[field] != null)
            ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [field] });
    };
    if (r.kind === 'parent_child') {
        require('parent_role', 'parent_role_required');
        require('parent_basis', 'parent_basis_required');
        for (const field of ['union_type', 'end_reason', 'sibling_basis']) {
            forbid(field, `${field}_not_for_parent_child`);
        }
    }
    else if (r.kind === 'union') {
        require('union_type', 'union_type_required');
        for (const field of ['parent_role', 'parent_basis', 'sibling_basis']) {
            forbid(field, `${field}_not_for_union`);
        }
        if (r.end_reason == null && !isFuzzyDateEmpty(r.ended)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'end_reason_required_for_end_date',
                path: ['end_reason'],
            });
        }
    }
    else {
        require('sibling_basis', 'sibling_basis_required');
        for (const field of ['parent_role', 'parent_basis', 'union_type', 'end_reason']) {
            forbid(field, `${field}_not_for_sibling`);
        }
        for (const field of ['started', 'ended']) {
            if (!isFuzzyDateEmpty(r[field])) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'sibling_has_no_dates',
                    path: [field],
                });
            }
        }
    }
    if (!fuzzyDateNotAfter(r.started, r.ended)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'ended_before_started', path: ['ended'] });
    }
    if (r.person_a_id != null && r.person_a_id === r.person_b_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'self_relation', path: ['person_b_id'] });
    }
});
/** POST /persons — the person, optionally with the relations that place them. */
export const createPersonSchema = z
    .object({
    person: personInputSchema,
    relations: z.array(relationInputSchema).max(20).optional(),
    summary: summarySchema.optional(),
    source_note: sourceNoteSchema,
})
    .strict();
/** PATCH /persons/:id — a sparse change-set over the same writable fields. */
export const updatePersonSchema = z
    .object({
    changes: personWritableSchema
        .extend({ portrait_photo_id: uuidSchema.nullable().optional() })
        .partial()
        .strict()
        .refine((c) => Object.keys(c).length > 0, { message: 'no_changes_supplied' }),
    summary: summarySchema.optional(),
    source_note: sourceNoteSchema,
})
    .strict();
/** PATCH /relations/:id — endpoints and `kind` are not editable (docs/05 §6.3). */
export const updateRelationSchema = z
    .object({
    changes: relationWritableSchema
        .omit({ kind: true, person_a_id: true, person_b_id: true })
        .partial()
        .strict()
        .refine((c) => Object.keys(c).length > 0, { message: 'no_changes_supplied' }),
    summary: summarySchema.optional(),
})
    .strict();
/** DELETE /persons/:id and /relations/:id — soft delete, reason required. */
export const retireSchema = z
    .object({
    retire_reason: z.enum(RETIRE_REASON),
    superseded_by_id: uuidSchema.nullable().optional(),
    summary: summarySchema.optional(),
})
    .strict()
    .refine((r) => r.retire_reason !== 'superseded' || r.superseded_by_id != null, {
    message: 'superseded_by_required',
    path: ['superseded_by_id'],
});
/** POST /relations — one relation, both endpoints known. */
export const createRelationSchema = z
    .object({
    relation: relationInputSchema.refine((r) => r.person_a_id != null && r.person_b_id != null, {
        message: 'endpoints_required',
    }),
    summary: summarySchema.optional(),
    /**
     * Replace an assertion with a corrected one, in a single transaction.
     *
     * This is for *corrections* — "that union row has the wrong dates, here is
     * the right one" — not for remarriage, which since migration 14 is simply a
     * second union row (the earlier one ended, the later one open). Doing a
     * correction as two calls has an ordering trap with no way out: retiring as
     * `superseded` demands the id of the row that supersedes it, which does not
     * exist yet. Naming the superseded row here closes the loop: retire, insert,
     * link. See ARCHITECTURE.md §2.1 C24.
     */
    supersedes_relation_id: uuidSchema.optional(),
})
    .strict();
/** A repeatable enum query param: `?status=pending&status=validated`. */
function repeatable(values) {
    return z
        .union([z.enum(values), z.array(z.enum(values))])
        .optional()
        .transform((v) => (v == null ? undefined : Array.isArray(v) ? v : [v]));
}
/** docs/05 §5.2. `rejected`/`retired` are not listable — use `/persons/:id/history`. */
export const listPersonsQuerySchema = pageQuerySchema.extend({
    q: z.string().trim().min(1).max(120).optional(),
    status: repeatable(['pending', 'validated']),
    gender: repeatable(GENDER),
    living_status: repeatable(LIVING_STATUS),
    birth_year_min: z.coerce.number().int().min(FUZZY_YEAR_MIN).max(FUZZY_YEAR_MAX).optional(),
    birth_year_max: z.coerce.number().int().min(FUZZY_YEAR_MIN).max(FUZZY_YEAR_MAX).optional(),
    has_user: z.enum(['true', 'false']).optional(),
    sort: z.enum(['family_name', 'birth', '-birth', 'created_at', '-created_at']).optional(),
});
/** docs/05 §6.5. */
export const listRelationsQuerySchema = pageQuerySchema.extend({
    kind: repeatable(RELATION_KIND),
    role: z.enum(['parent', 'child']).optional(),
    status: repeatable(['pending', 'validated']),
    sort: z.enum(['started', '-started', 'created_at']).default('started'),
});
/** PUT /proposals/:id/vote — docs/05 §9.3. */
export const castVoteSchema = z
    .object({
    choice: z.enum(VOTE_CHOICE),
    comment: z.string().trim().max(VOTE_COMMENT_MAX_LENGTH).nullable().optional(),
})
    .strict();
/** POST /proposals/:id/withdraw — docs/05 §9.5. */
export const withdrawProposalSchema = z
    .object({ reason: z.string().trim().max(SUMMARY_MAX_LENGTH).nullable().optional() })
    .strict();
/** GET /proposals — docs/05 §9.1. */
export const listProposalsQuerySchema = pageQuerySchema.extend({
    filter: z.enum(['open', 'mine', 'needs_my_vote', 'all']).default('open'),
    status: repeatable(PROPOSAL_STATUS),
    kind: repeatable(PROPOSAL_KIND),
    subject_person_id: uuidSchema.optional(),
    sort: z.enum(['-opened_at', 'opened_at', 'closes_at']).default('-opened_at'),
});
/** POST /council/seats — docs/05 §9.7. `seat_no` omitted = lowest vacant number. */
export const grantSeatSchema = z
    .object({
    holder_user_id: uuidSchema,
    seat_no: z.number().int().min(1).max(99).optional(),
})
    .strict();
export const listSeatsQuerySchema = pageQuerySchema.extend({
    include_history: z.enum(['true', 'false']).optional(),
});
/**
 * PATCH /settings — docs/05 §9.10. `bootstrap_mode` and `graph_version` are absent
 * on purpose: the first self-terminates (docs/07 §6.4), the second is bumped by
 * the database when validated structure changes.
 */
export const updateSettingsSchema = z
    .object({
    changes: z
        .object({
        council_size: z.number().int().min(1).max(99).optional(),
        approval_threshold: z.number().int().min(1).max(99).optional(),
        voting_window_days: z.number().int().min(1).max(365).optional(),
    })
        .strict()
        .refine((c) => Object.keys(c).length > 0, { message: 'no_changes_supplied' }),
    /** Optimistic-concurrency guard; mismatch → 409 `stale_settings`. */
    expected_updated_at: z.string().datetime({ offset: true }).optional(),
})
    .strict();
/** GET /notifications — docs/05 §10.1. */
export const listNotificationsQuerySchema = pageQuerySchema.extend({
    unread: z.enum(['true', 'false']).optional(),
    kind: repeatable(NOTIFICATION_KIND),
});
/* ----------------------------------------------------------------- audit log */
/**
 * GET /audit-log — docs/05 §13.1, admin only.
 *
 * `action` accepts an exact `namespace.verb` or a namespace prefix with a
 * trailing `*` (`proposal.*`); `actor_user_id=null` selects the system-job rows
 * a worker wrote with no actor. Sort is fixed `-at`.
 */
export const listAuditQuerySchema = pageQuerySchema.extend({
    action: z
        .string()
        .trim()
        .max(64)
        .regex(/^[a-z_]+\.([a-z_]+|\*)$/, 'invalid_action_filter')
        .optional(),
    entity_type: repeatable(AUDIT_ENTITY_TYPE),
    entity_id: uuidSchema.optional(),
    actor_user_id: z.union([uuidSchema, z.literal('null')]).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
/* --------------------------------------------------------------- kinship */
/**
 * docs/05 §6.6 — the derived-relations panel. Nothing here is stored: every
 * field is computed from the graph snapshot on the way out (ADR-0003).
 */
export const derivedRelationsQuerySchema = pageQuerySchema.extend({
    kind: repeatable(DERIVED_KIND),
});
/** docs/05 §7.2 — "show me all of X's cousins". `kind` is required here. */
export const relativesQuerySchema = pageQuerySchema.extend({
    kind: z.enum(DERIVED_KIND),
    degree: z.coerce.number().int().min(0).max(20).optional(),
    removed: z.coerce.number().int().min(0).max(20).optional(),
});
/** docs/05 §7.3 — lineal closure, grouped by generation. Not paginated. */
export const lineageQuerySchema = z.object({
    depth: z.coerce.number().int().min(1).max(10).default(3),
    basis: repeatable(['biological', 'adoptive', 'foster', 'guardianship', 'unknown']),
});
/** docs/05 §7.1 — the A↔B finder. */
export const kinshipPathQuerySchema = z
    .object({
    from: uuidSchema,
    to: uuidSchema,
    mode: z.enum(['shortest', 'all']).default('shortest'),
    max_paths: z.coerce.number().int().min(1).max(25).default(25),
})
    .refine((q) => q.from !== q.to, { message: 'from_and_to_must_differ', path: ['to'] });
/* ----------------------------------------------------------------- photos */
export const PHOTO_CAPTION_MAX_LENGTH = 500;
/**
 * docs/05 §8.4 — caption and date are **tri-state**: absent leaves the field
 * alone, `null` clears it, a value sets it. That distinction is the whole point
 * of the endpoint: "we never recorded when this was taken" and "we recorded that
 * we do not know" are the same to a reader, but only one of them is a change.
 */
export const photoPatchSchema = z
    .object({
    caption: z.string().trim().max(PHOTO_CAPTION_MAX_LENGTH).nullable().optional(),
    taken: fuzzyDateSchema.nullable().optional(),
})
    .strict()
    .refine((body) => Object.keys(body).length > 0, { message: 'no_changes' });
/** docs/05 §8.6. */
export const attachSubjectSchema = z
    .object({ person_id: uuidSchema, is_primary: z.boolean().default(false) })
    .strict();
/** The `subjects` multipart part of docs/05 §8.1, parsed from its JSON text. */
export const uploadSubjectsSchema = z
    .array(z.object({ person_id: uuidSchema, is_primary: z.boolean().optional() }).strict())
    .max(50);
/** docs/05 §8.8 — a person's gallery. */
export const galleryQuerySchema = pageQuerySchema.extend({
    filter: z.enum(['all', 'dated', 'undated']).default('all'),
});
/* ------------------------------------------------- recording a kinship claim */
/**
 * The shapes a family actually states out loud — "these two are cousins",
 * "he's her uncle" — expressed as the path that makes them true.
 *
 * FamTree never stores a derived relationship (ADR-0003): cousinhood is a
 * *consequence* of parent edges, not a fact in its own right. But a family that
 * says "these two are first cousins" is stating something real and often knows
 * nothing else — not the grandparent's name, sometimes not even the parents'.
 * The way to honour that claim without corrupting the model is to record the
 * atomic facts it implies: the connecting people (unnamed, if that is the truth)
 * and the parent edges between them. Then the engine derives "first cousin"
 * from evidence, the way it derives every other label.
 *
 * `up`/`down` are the `U^a D^b` counts of docs/06 §3.1 — the number of parent
 * steps from each side to the ancestor they share.
 */
export const KIN_CLAIMS = {
    sibling: { up: 1, down: 1 },
    half_sibling: { up: 1, down: 1 },
    grandparent: { up: 2, down: 0 },
    grandchild: { up: 0, down: 2 },
    pibling: { up: 2, down: 1 },
    nibling: { up: 1, down: 2 },
    first_cousin: { up: 2, down: 2 },
    first_cousin_once_removed: { up: 2, down: 3 },
    second_cousin: { up: 3, down: 3 },
};
export const KIN_CLAIM_NAMES = Object.keys(KIN_CLAIMS);
/**
 * The ladder that makes a stated claim true.
 *
 * The shape is always the V of docs/06 §2.2 — `up` parent steps from one side,
 * `down` from the other, meeting at one shared ancestor. Three cases, and the
 * degenerate ones are where a naive version goes wrong:
 *
 *  - **both sides climb** (siblings, cousins, aunts): the apex is the top of the
 *    `from` chain, and the `to` chain stops one rung short and hangs off it.
 *  - **`down` is zero** (B *is* A's grandparent): B is the apex, so the `from`
 *    chain needs one rung fewer and its top hangs off B.
 *  - **`up` is zero** (B is A's grandchild): the mirror of that.
 *
 * A person with exactly one recorded parent gets that parent as their first
 * rung, so stating a claim about people already half-entered adds only what is
 * genuinely missing.
 */
export function planKinshipSkeleton(args) {
    const { up, down } = KIN_CLAIMS[args.claim];
    const slots = [];
    const ambiguous = [];
    const links = [];
    // How many rungs each side owns. The side carrying the apex owns it; the other
    // stops one short, because that rung *is* the apex.
    const fromRungs = down === 0 ? Math.max(up - 1, 0) : up;
    const toRungs = down === 0 ? 0 : Math.max(down - 1, 0);
    const endpoint = (side) => ({ kind: 'endpoint', side });
    const build = (side, rungs, ownParents) => {
        for (let generation = 1; generation <= rungs; generation += 1) {
            const chosen = args.chosen?.[`${side}:${String(generation)}`];
            const slot = { side, generation, personId: chosen ?? null, apex: false };
            // Only the first rung can be read off the tree: beyond it no person has
            // been settled on yet, so there is nothing to look up.
            if (slot.personId === null && generation === 1) {
                if (ownParents.length === 1)
                    slot.personId = ownParents[0];
                else if (ownParents.length > 1)
                    ambiguous.push(slot);
            }
            slots.push(slot);
            links.push({
                parent: { kind: 'slot', side, generation },
                child: generation === 1 ? endpoint(side) : { kind: 'slot', side, generation: generation - 1 },
                // The only edge we can already know about is the one we just read off
                // the tree: this person's own recorded parent.
                existing: generation === 1 && slot.personId !== null && ownParents.includes(slot.personId),
            });
        }
    };
    build('from', fromRungs, args.fromParents);
    build('to', toRungs, args.toParents);
    if (down === 0) {
        // `to` is the ancestor: the top of the `from` ladder is their child.
        const topOfFrom = fromRungs === 0 ? endpoint('from') : { kind: 'slot', side: 'from', generation: fromRungs };
        links.push({ parent: endpoint('to'), child: topOfFrom, existing: false });
    }
    else if (up === 0) {
        // `from` is the ancestor: the mirror.
        const topOfTo = toRungs === 0 ? endpoint('to') : { kind: 'slot', side: 'to', generation: toRungs };
        links.push({ parent: endpoint('from'), child: topOfTo, existing: false });
    }
    else {
        const apexSlot = slots.find((slot) => slot.side === 'from' && slot.generation === up);
        if (apexSlot)
            apexSlot.apex = true;
        const topOfTo = toRungs === 0 ? endpoint('to') : { kind: 'slot', side: 'to', generation: toRungs };
        links.push({
            parent: { kind: 'slot', side: 'from', generation: up },
            child: topOfTo,
            existing: false,
        });
    }
    return { slots, links, ambiguous };
}
/* -------------------------------------------------------------- the tree view */
/** docs/05 §7.4 (added in M5) — the bounded subgraph a chart draws. */
export const treeQuerySchema = z.object({
    focus: uuidSchema,
    /** Generations either side of the pivot (docs/08 §4.7 defaults to 4). */
    depth: z.coerce.number().int().min(1).max(8).default(4),
    /** Elder/admin toggle: overlay the `pending` rows touching the visible people. */
    include_pending: z.enum(['true', 'false']).default('false'),
});
/* ------------------------------------------------- finding a person by lineage */
/**
 * Particles that mean "child of" — the way most of the world's families
 * actually name someone.
 *
 * A tree recorded in Morocco is not searched by surname. People are identified
 * by descent — *Hassan ben Idriss ben Mehdi* — and the surname, where there is
 * one, is often shared by half the village. `ben`/`bin`/`ibn` and the Hassaniya
 * `ould`/`mint` all carry the same meaning; `bent`/`bint` mark the child as a
 * daughter rather than naming a different parent, so they parse identically.
 * The English forms are here so the box works for whoever is typing.
 */
export const LINEAGE_PARTICLES = [
    'ben',
    'bin',
    'ibn',
    'bent',
    'bint',
    'ould',
    'mint',
    'ould.',
    'b.',
    'son of',
    'daughter of',
    'child of',
];
/**
 * Parse "Hassan ben Idriss ben m:Zohra" into segments.
 *
 * Prefix a name with `f:` or `m:` (or `father:`/`mother:`) to pin which parent
 * it names — the escape hatch for the case that actually needs it: two parents
 * whose names both fit, or a maternal line recorded because the father is
 * unknown. Everything else is left open.
 */
export function parseLineageQuery(input) {
    const particles = [...LINEAGE_PARTICLES]
        .sort((a, b) => b.length - a.length)
        .map((particle) => particle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    // `>` is the compact form, for anyone who would rather not type a word.
    const splitter = new RegExp(`\\s+(?:${particles.join('|')})\\s+|\\s*>\\s*`, 'gi');
    return input
        .split(splitter)
        .map((raw) => raw.trim())
        .filter((raw) => raw.length > 0)
        .map((raw) => {
        const match = /^(f|m|father|mother)\s*:\s*(.+)$/i.exec(raw);
        if (!match)
            return { name: raw, side: 'any' };
        const side = match[1].toLowerCase().startsWith('f') ? 'father' : 'mother';
        return { name: match[2].trim(), side };
    })
        .filter((segment) => segment.name.length > 0);
}
/** docs/05 §5.8 (added in M5.5) — find a person by their line of descent. */
export const lineageSearchSchema = z.object({
    q: z.string().trim().min(1).max(400),
    limit: z.coerce.number().int().min(1).max(25).default(10),
});
/* --------------------------------------------------------- claiming an identity */
/** docs/05 §11.4 (added in M5.5) — "that person in the tree is me". */
export const claimIdentitySchema = z.object({ person_id: uuidSchema }).strict();
/** An administrator's answer to a claim. */
export const resolveClaimSchema = z
    .object({ decision: z.enum(['approve', 'refuse']), note: z.string().trim().max(500).optional() })
    .strict();
//# sourceMappingURL=schemas.js.map